import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from './config';

// Global error deduplication
const errorLogsShown = new Set();
const activeSubscriptions = new Map();
const migrationAttempted = new Set();

// Function to clear error logs (call on user logout)
export const clearErrorLogs = () => {
  errorLogsShown.clear();
  // Unsubscribe from all active subscriptions
  activeSubscriptions.forEach(unsubscribe => unsubscribe());
  activeSubscriptions.clear();
  // Clear migration attempts when user logs out
  migrationAttempted.clear();
};

// Lists collection operations
export const createList = async (listData) => {
  try {
    const docRef = await addDoc(collection(db, 'lists'), {
      ...listData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating list:', error);
    return { success: false, error: error.message };
  }
};

export const updateList = async (listId, updates) => {
  try {
    await updateDoc(doc(db, 'lists', listId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteList = async (listId) => {
  try {
    await deleteDoc(doc(db, 'lists', listId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getList = async (listId, isDeleting = false) => {
  try {
    const docSnap = await getDoc(doc(db, 'lists', listId));
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      // If we're in the process of deleting, return success to avoid error toast
      if (isDeleting) {
        return { success: true, data: null };
      }
      return { success: false, error: 'List not found' };
    }
  } catch (error) {
    const errorKey = `getList-${listId}-${error.code || 'unknown'}`;
    if (!errorLogsShown.has(errorKey)) {
      errorLogsShown.add(errorKey);
      console.error('getList: Error fetching list:', error);
    }
    return { success: false, error: error.message };
  }
};

export const getUserLists = async (userId) => {
  try {
    const q = query(
      collection(db, 'lists'),
      where('members', 'array-contains', userId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const lists = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, data: lists };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const joinSharedList = async (listId, userId) => {
  try {
    const listRef = doc(db, 'lists', listId);
    await updateDoc(listRef, {
      members: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error joining shared list:', error);
    return { success: false, error: error.message };
  }
};

export const leaveList = async (listId, userId) => {
  try {
    const listRef = doc(db, 'lists', listId);
    await updateDoc(listRef, {
      members: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error leaving list:', error);
    return { success: false, error: error.message };
  }
};

// Wishes subcollection operations
export const addWish = async (listId, wishData, createdBy) => {
  try {
    console.log('addWish: Adding wish to list', listId, 'with data:', wishData);
    const docRef = await addDoc(collection(db, 'lists', listId, 'wishes'), {
      ...wishData,
      createdBy: createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('addWish: Wish document created with ID:', docRef.id);
    
    // Update the list's wish count
    await updateDoc(doc(db, 'lists', listId), {
      wishCount: increment(1),
      updatedAt: serverTimestamp()
    });
    console.log('addWish: List wish count updated');
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('addWish: Error adding wish:', error);
    return { success: false, error: error.message };
  }
};

export const updateWish = async (listId, wishId, updates) => {
  try {
    await updateDoc(doc(db, 'lists', listId, 'wishes', wishId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteWish = async (listId, wishId) => {
  try {
    // Delete the wish from the subcollection
    await deleteDoc(doc(db, 'lists', listId, 'wishes', wishId));
    
    // Update the list's wish count
    await updateDoc(doc(db, 'lists', listId), {
      wishCount: increment(-1),
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getListWishes = async (listId) => {
  try {
    const q = query(
      collection(db, 'lists', listId, 'wishes'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const wishes = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, data: wishes };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getListWishCounts = async (listId) => {
  try {
    const q = query(
      collection(db, 'lists', listId, 'wishes')
    );
    const querySnapshot = await getDocs(q);
    const wishes = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const totalWishes = wishes.length;
    const boughtWishes = wishes.filter(wish => wish.boughtBy && wish.boughtBy.length > 0).length;
    const plannedWishes = wishes.filter(wish => wish.plannedBy && wish.plannedBy.length > 0).length;
    
    return { 
      success: true, 
      data: { 
        total: totalWishes, 
        bought: boughtWishes, 
        planned: plannedWishes,
        isComplete: totalWishes > 0 && boughtWishes === totalWishes
      } 
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Gift tracking operations
export const markGiftAsPlanned = async (listId, wishId, userId) => {
  try {
    await updateDoc(doc(db, 'lists', listId, 'wishes', wishId), {
      plannedBy: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const markGiftAsBought = async (listId, wishId, userId) => {
  try {
    await updateDoc(doc(db, 'lists', listId, 'wishes', wishId), {
      plannedBy: arrayRemove(userId),
      boughtBy: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const unmarkGift = async (listId, wishId, userId) => {
  try {
    await updateDoc(doc(db, 'lists', listId, 'wishes', wishId), {
      plannedBy: arrayRemove(userId),
      boughtBy: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Migration function to move wishes from old structure to new subcollection
const migrateWishesToSubcollection = async (listId) => {
  // Prevent multiple migration attempts for the same list
  if (migrationAttempted.has(listId)) {
    console.log(`Migration already attempted for list ${listId}, skipping`);
    return;
  }
  migrationAttempted.add(listId);
  console.log(`Starting migration for list ${listId}`);
  
  try {
    // Check if there are wishes in the old structure
    const oldWishesQuery = query(
      collection(db, 'wishes'),
      where('listId', '==', listId)
    );
    const oldWishesSnapshot = await getDocs(oldWishesQuery);
    
    console.log(`Found ${oldWishesSnapshot.docs.length} wishes in old structure for list ${listId}`);
    
    if (oldWishesSnapshot.docs.length > 0) {
      console.log(`Migrating ${oldWishesSnapshot.docs.length} wishes for list ${listId}`);
      
      // Move each wish to the subcollection
      const migrationPromises = oldWishesSnapshot.docs.map(async (wishDoc) => {
        const wishData = wishDoc.data();
        
        // Remove listId since it's now implicit in the subcollection
        const { listId: _, ...wishDataWithoutListId } = wishData;
        
        // Add createdBy field if it doesn't exist (for backward compatibility)
        const migratedWishData = {
          ...wishDataWithoutListId,
          createdBy: wishDataWithoutListId.createdBy || wishDataWithoutListId.addedBy || 'unknown'
        };
        
        // Add to subcollection
        await addDoc(collection(db, 'lists', listId, 'wishes'), migratedWishData);
        
        // Delete from old structure
        await deleteDoc(wishDoc.ref);
      });
      
      await Promise.all(migrationPromises);
      console.log(`Successfully migrated ${oldWishesSnapshot.docs.length} wishes for list ${listId}`);
    } else {
      console.log(`No wishes found in old structure for list ${listId}`);
    }
  } catch (error) {
    console.error('Error migrating wishes for list', listId, ':', error);
    // Remove from attempted set so it can be retried
    migrationAttempted.delete(listId);
  }
};

// Real-time listeners
export const subscribeToList = (listId, callback) => {
  // Check if we already have an active subscription for this list
  const existingKey = `list-${listId}`;
  if (activeSubscriptions.has(existingKey)) {
    // If we have an existing subscription, we need to get the current data
    // and call the callback immediately, then return the existing unsubscribe function
    try {
      getDoc(doc(db, 'lists', listId)).then((docSnap) => {
        if (docSnap.exists()) {
          callback({ id: docSnap.id, ...docSnap.data() });
        } else {
          callback(null);
        }
      });
    } catch (error) {
      console.error('Error fetching current list for existing subscription:', error);
      callback(null);
    }
    return activeSubscriptions.get(existingKey);
  }
  
  const listRef = doc(db, 'lists', listId);
  
  const unsubscribe = onSnapshot(listRef, (docSnap) => {
    if (docSnap.exists()) {
      const listData = { id: docSnap.id, ...docSnap.data() };
      callback(listData);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error in list subscription for list', listId, ':', error);
    callback(null);
  });
  
  // Store the subscription for deduplication
  activeSubscriptions.set(existingKey, unsubscribe);
  
  return unsubscribe;
};

export const subscribeToListWishes = async (listId, callback) => {
  // Check if we already have an active subscription for this list
  const existingKey = `listWishes-${listId}`;
  if (activeSubscriptions.has(existingKey)) {
    // If we have an existing subscription, we need to get the current data
    // and call the callback immediately, then return the existing unsubscribe function
    try {
      const q = query(
        collection(db, 'lists', listId, 'wishes'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const wishes = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });
      callback(wishes);
    } catch (error) {
      console.error('Error fetching current wishes for existing subscription:', error);
      callback([]);
    }
    return activeSubscriptions.get(existingKey);
  }
  
  // First, try to migrate any existing wishes from old structure
  await migrateWishesToSubcollection(listId);
  
  const q = query(
    collection(db, 'lists', listId, 'wishes'),
    orderBy('createdAt', 'desc')
  );
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    console.log(`Wishes snapshot received for list ${listId}:`, querySnapshot.docs.length, 'wishes');
    const wishes = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      };
    });
    callback(wishes);
  }, (error) => {
    console.error('Error in wishes subscription for list', listId, ':', error);
    callback([]);
  });
  
  // Store the subscription for deduplication
  activeSubscriptions.set(existingKey, unsubscribe);
  
  return unsubscribe;
};

export const subscribeToUserLists = (userId, callback) => {
  // Clean up any existing subscription for this user
  const existingKey = `userLists-${userId}`;
  if (activeSubscriptions.has(existingKey)) {
    activeSubscriptions.get(existingKey)();
    activeSubscriptions.delete(existingKey);
  }

  const q = query(
    collection(db, 'lists'),
    where('members', 'array-contains', userId),
    orderBy('updatedAt', 'desc')
  );
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const lists = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      };
    });
    callback(lists);
  }, (error) => {
    const errorKey = `subscribeToUserLists-${userId}-${error.code || 'unknown'}`;
    if (!errorLogsShown.has(errorKey)) {
      errorLogsShown.add(errorKey);
      console.error('subscribeToUserLists: Error in subscription:', error);
    }
    // Clear lists on permission error
    if (error.code === 'permission-denied') {
      callback([]);
    }
  });

  // Track this subscription
  activeSubscriptions.set(existingKey, unsubscribe);

  // Return a wrapper that also cleans up our tracking
  return () => {
    unsubscribe();
    activeSubscriptions.delete(existingKey);
  };
};
